import React, { useCallback, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import {
  Box,
  Typography,
  Stack,
} from '@fortress-technology-solutions/fortress-component-library/Atoms';
import intlMessages from '../messages';
import { NEEDS_HELP_STATUS } from '../statusFilters';

type Props = {|
  selectedStatuses: string[],
  onChange: (statuses: string[]) => void,
  isNeedsHelpFilterShown: boolean,
|};

function StatusFilter({
  selectedStatuses,
  onChange,
  isNeedsHelpFilterShown,
}: Props): React$Node {
  const options = useMemo(
    () => [
      {
        label: <FormattedMessage {...intlMessages.newStatus} />,
        value: 'NEW',
      },
      {
        label: <FormattedMessage {...intlMessages.resolvedStatus} />,
        value: 'RESOLVED',
      },
      {
        label: <FormattedMessage {...intlMessages.unresolvedStatus} />,
        value: 'UNRESOLVED',
      },
      ...(isNeedsHelpFilterShown
        ? [
            {
              label: <FormattedMessage {...intlMessages.needsHelpStatus} />,
              value: NEEDS_HELP_STATUS,
            },
          ]
        : []),
    ],
    [isNeedsHelpFilterShown],
  );

  const handleButtonClick = useCallback(
    (value: string) => {
      const isSelected = selectedStatuses.includes(value);
      const newStatuses = isSelected
        ? selectedStatuses.filter((status) => status !== value)
        : [...selectedStatuses, value];
      onChange(newStatuses);
    },
    [selectedStatuses, onChange],
  );

  return (
    <Stack mb={2}>
      <Typography variant="titleSmall">
        <FormattedMessage {...intlMessages.filterByStatus} />
      </Typography>
      <Box
        mt={0.5}
        mb={2}
        sx={{
          display: 'inline-flex',
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: '4px',
          overflow: 'hidden',
        }}
      >
        {options.map((option, index) => {
          const isSelected = selectedStatuses.includes(option.value);
          return (
            <Box
              key={option.value}
              onClick={() => handleButtonClick(option.value)}
              sx={{
                px: 3,
                py: 1,
                minHeight: 38,
                cursor: 'pointer',
                backgroundColor: isSelected
                  ? 'background.alt'
                  : 'background.paper',
                borderRight:
                  index < options.length - 1 ? '1px solid #dedede' : 'none',
                transition: 'background-color 0.2s',
                '&:hover': {
                  backgroundColor: isSelected
                    ? 'action.selected'
                    : 'action.hover',
                },
                minWidth: '110px',
                textAlign: 'center',
              }}
            >
              <Typography
                variant="bodyMedium"
                sx={{
                  color: isSelected ? 'primary.main' : 'text.secondary',
                  fontWeight: isSelected ? 500 : 400,
                }}
              >
                {option.label}
              </Typography>
            </Box>
          );
        })}
      </Box>
    </Stack>
  );
}

export default StatusFilter;
